import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { useCallback, useState, type ReactNode } from "react";
import { useBlocker } from "react-router";

export interface UnsavedCoachingChangesGuard {
  /**
   * Runs the action immediately when there are no unsaved changes; otherwise
   * stashes it behind an "Unsaved changes" confirmation.
   */
  requestAction: (action: () => void) => void;
  /** The confirmation dialogs; render once near the guarded UI. */
  guardModals: ReactNode;
}

const UNSAVED_CHANGES_BODY =
  "You have unsaved changes. Save them to keep your work, or discard them to continue.";

/**
 * Guards context switches away from an open manager-note/goal editor: switching
 * coaching weeks, opening another more-menu action, and in-app navigation all
 * confirm before discarding the draft.
 */
export function useUnsavedCoachingChangesGuard(
  isDirty: boolean,
): UnsavedCoachingChangesGuard {
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Block in-app navigation while an editor has unsaved changes.
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  const requestAction = useCallback(
    (action: () => void) => {
      if (isDirty) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [isDirty],
  );

  const guardModals = (
    <>
      <ConfirmationModal
        isOpen={pendingAction !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setPendingAction(null);
          }
        }}
        onConfirm={() => {
          pendingAction?.();
          setPendingAction(null);
        }}
      >
        {UNSAVED_CHANGES_BODY}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={navigationBlocker.state === "blocked"}
        onOpenChange={(isOpen) => {
          if (!isOpen && navigationBlocker.state === "blocked") {
            navigationBlocker.reset();
          }
        }}
        onConfirm={() => {
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
          }
        }}
      >
        {UNSAVED_CHANGES_BODY}
      </ConfirmationModal>
    </>
  );

  return { requestAction, guardModals };
}
